import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  defaultThemeConfig, 
  isValidTheme, 
  getOppositeTheme,
  saveThemeToStorage,
  loadThemeFromStorage,
  generateThemeCSSVariables
} from './tokens';

// Create theme context
export const ThemeContext = createContext(undefined);

// Theme provider component
export const ThemeProvider = ({ 
  children, 
  config: userConfig = {} 
}) => {
  // Merge user config with default config
  const config = {
    ...defaultThemeConfig,
    ...userConfig,
  };

  // Force light mode only - remove dark mode support
  const [theme, setThemeState] = useState('light');

  // Update CSS custom properties when theme changes
  useEffect(() => {
    const cssVariables = generateThemeCSSVariables(theme);
    
    // Apply CSS variables to document root
    const root = document.documentElement;
    Object.entries(cssVariables).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });

    // Ensure dark mode class is removed (light mode only)
    root.classList.remove('dark');
  }, [theme]);

  // Computed values - always light mode
  const isDark = false;

  // Context value - light mode only
  const contextValue = {
    theme: 'light',
    config,
    toggleTheme: () => {}, // Disabled - no theme switching
    setTheme: () => {}, // Disabled - no theme switching
    isDark,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
};

// Theme toggle component
export const ThemeToggle = ({ 
  className = '', 
  showLabel = false 
}) => {
  const { theme, toggleTheme, isDark } = useTheme();

  return (
    <button
      onClick={toggleTheme}
      className={`
        inline-flex items-center gap-2 px-3 py-2 rounded-md
        bg-white dark:bg-gray-800
        text-gray-900 dark:text-gray-100
        border border-gray-200 dark:border-gray-700
        hover:bg-gray-50 dark:hover:bg-gray-700
        focus:outline-none focus:ring-2 focus:ring-blue-500
        transition-colors duration-200
        ${className}
      `}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
    >
      {/* Sun icon for light mode */}
      {isDark ? (
        <svg 
          className="w-5 h-5" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" 
          />
        </svg>
      ) : (
        // Moon icon for dark mode
        <svg 
          className="w-5 h-5" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" 
          />
        </svg>
      )}
      
      {showLabel && (
        <span className="text-sm font-medium">
          {isDark ? 'Light' : 'Dark'}
        </span>
      )}
    </button>
  );
};

export default ThemeProvider;
